import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Table,
  Header,
  Icon,
  Label,
  Dropdown,
  Input,
  Button,
  Segment,
  Message,
} from 'semantic-ui-react';
import { SG_GET_SLACK_WORKSPACE_TASKS } from 'constants/actions';
import Navigation from './Navigation';
import { useWorkspaceId } from './useWorkspaceId';

function TaskList() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceId = useWorkspaceId();
  const hasFetchedRef = useRef(false);
  
  const { workspaceTasks, workspaceTasksTotal, workspaceTasksPending } = useSelector(
    (s) => s.slack_tasks || {},
    shallowEqual
  );

  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
    search: searchParams.get('search') || '',
  });

  useEffect(() => {
    if (!workspaceId) return;
    
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.search) params.append('search', filters.search);
    params.append('sortBy', 'updated_at');
    params.append('sortOrder', 'desc');

    dispatch({
      type: SG_GET_SLACK_WORKSPACE_TASKS,
      payload: { workspaceId, queryParams: params.toString() },
    });
  }, [workspaceId, filters.status, filters.priority, filters.search, dispatch]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: '', priority: '', search: '' });
    setSearchParams({});
  };

  const statusOptions = [
    { key: 'all', text: 'All Statuses', value: '' },
    { key: 'todo', text: 'To Do', value: 'todo' },
    { key: 'in_progress', text: 'In Progress', value: 'in_progress' },
    { key: 'done', text: 'Done', value: 'done' },
  ];

  const priorityOptions = [
    { key: 'all', text: 'All Priorities', value: '' },
    { key: '1', text: '🔴 Urgent', value: '1' },
    { key: '2', text: '🟠 High', value: '2' },
    { key: '3', text: '🟡 Medium', value: '3' },
    { key: '4', text: '⚪ Low', value: '4' },
  ];

  const getPriorityLabel = (priority) => {
    const labels = {
      1: { color: 'red', text: 'Urgent' },
      2: { color: 'orange', text: 'High' },
      3: { color: 'yellow', text: 'Medium' },
      4: { color: 'grey', text: 'Low' },
    };
    return labels[priority] || { color: 'grey', text: 'N/A' };
  };

  const getStatusLabel = (status) => {
    const labels = {
      todo: { color: 'grey', text: 'To Do', icon: 'circle outline' },
      in_progress: { color: 'blue', text: 'In Progress', icon: 'spinner' },
      done: { color: 'green', text: 'Done', icon: 'check circle' },
    };
    return labels[status] || { color: 'grey', text: status, icon: 'circle' };
  };

  const tasks = Array.isArray(workspaceTasks) ? workspaceTasks : [];

  return (
    <Page>
      <Navigation />

      <Header as="h1">
        <Icon name="list" />
        All Tasks
        <Header.Subheader>
          {workspaceTasksTotal || 0} total tasks
        </Header.Subheader>
      </Header>

      {/* Filters */}
      <Segment>
        <FilterRow>
          <Input
            icon="search"
            placeholder="Search tasks..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            style={{ width: '300px' }}
          />
          <Dropdown
            selection
            options={statusOptions}
            value={filters.status}
            onChange={(e, { value }) => handleFilterChange('status', value)}
            placeholder="Filter by status"
          />
          <Dropdown
            selection
            options={priorityOptions}
            value={filters.priority}
            onChange={(e, { value }) => handleFilterChange('priority', value)}
            placeholder="Filter by priority"
          />
          <Button basic onClick={clearFilters}>
            <Icon name="remove" />
            Clear Filters
          </Button>
        </FilterRow>
      </Segment>

      {/* Tasks Table */}
      {workspaceTasksPending ? (
        <Message icon>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Loading tasks...</Message.Header>
          </Message.Content>
        </Message>
      ) : tasks.length === 0 ? (
        <Message info>
          <Message.Header>No tasks found</Message.Header>
          <p>Try adjusting your filters or create a new task.</p>
        </Message>
      ) : (
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>ID</Table.HeaderCell>
              <Table.HeaderCell>Title</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Priority</Table.HeaderCell>
              <Table.HeaderCell>Assigned To</Table.HeaderCell>
              <Table.HeaderCell>Due Date</Table.HeaderCell>
              <Table.HeaderCell>Created</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {tasks.map((task) => {
              const statusLabel = getStatusLabel(task.status);
              const priorityLabel = getPriorityLabel(task.priority);

              return (
                <Table.Row key={task.id}>
                  <Table.Cell>
                    <code>{task.identifier || task.id.slice(0, 8)}</code>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>{task.title}</strong>
                    {task.description && (
                      <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                        {task.description.slice(0, 100)}
                        {task.description.length > 100 && '...'}
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Label color={statusLabel.color}>
                      <Icon name={statusLabel.icon} />
                      {statusLabel.text}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Label color={priorityLabel.color}>{priorityLabel.text}</Label>
                  </Table.Cell>
                  <Table.Cell>
                    {task.assignedTo?.name || 'Unassigned'}
                  </Table.Cell>
                  <Table.Cell>
                    {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {task.createdAt ? new Date(task.createdAt).toLocaleDateString() : '-'}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </Page>
  );
}

const Page = styled.div`
  padding: 20px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

export default TaskList;
